import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  bootstrapSession,
  clearSession,
  registerUnauthorizedHandler,
  setToken,
  setRefreshToken,
} from "../api/client";
import { fetchMe, login as loginReq, logoutServer, useProfile } from "../api/auth";
import type { AuthResponse, LoginPayload, User } from "../api/types";
import { LANG_STORAGE_KEY, normalizeLanguage } from "../i18n";
import { identify, resetAnalytics } from "../analytics";
import { clearWizard } from "../features/generate/wizardStorage";

interface AuthState {
  user: User | null;
  /** True until we've resolved the persisted token on first load. */
  initializing: boolean;
  isAuthenticated: boolean;
  /**
   * POST /auth/login. Returns the raw AuthResponse. When `must_change_password` is
   * true the restricted token is stored but the user is NOT authenticated — the caller
   * must route to /set-password.
   */
  login: (payload: LoginPayload) => Promise<AuthResponse>;
  /** Given a change-password AuthResponse (full token + user), complete the login. */
  completePasswordChange: (res: AuthResponse) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

// One shared channel for cross-tab auth events (logout). Null where BroadcastChannel is unsupported
// (Safari < 15.4) — the logout effect falls back to a localStorage ping there.
const authChannel: BroadcastChannel | null =
  typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("eddkit.auth") : null;

function _identifyUser(u: User): void {
  try {
    identify(u.id, {
      org_id: u.org_id ?? undefined,
      location_id: (u as User & { location_id?: string }).location_id ?? undefined,
      role: u.role,
      // 'plan' may not exist on User — gracefully omit when absent.
      plan: (u as User & { settings?: { plan?: string } }).settings?.plan ?? "free",
    });
  } catch {
    // analytics must never surface errors to auth layer
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { i18n } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [initializing, setInitializing] = useState(true);

  // Once authenticated, the profile carries the server-side language preference.
  const profileQ = useProfile(!!user);

  // Seed i18n from the server preference ONLY when the user hasn't set an explicit
  // local choice. localStorage wins (the i18next detector already applied it on boot);
  // server settings are the cross-device fallback before the browser default / "en".
  useEffect(() => {
    const serverLang = profileQ.data?.settings?.language;
    if (!serverLang) return;
    let hasLocal: boolean;
    try {
      hasLocal = !!localStorage.getItem(LANG_STORAGE_KEY);
    } catch {
      hasLocal = false;
    }
    if (hasLocal) return;
    const lang = normalizeLanguage(serverLang);
    if (normalizeLanguage(i18n.language) !== lang) {
      void i18n.changeLanguage(lang);
    }
  }, [profileQ.data?.settings, i18n]);

  const logout = useCallback(() => {
    // Revoke this device's refresh session server-side + clear the cookie (best-effort, never
    // blocks/throws), then clear local state and tell other tabs.
    void logoutServer();
    clearSession();
    setUser(null);
    qc.clear();
    resetAnalytics();
    // Drop any in-progress generation wizard so the next user in this tab can't see (or inherit) the
    // previous user's persisted work orders / selections. sessionStorage survives logout otherwise.
    clearWizard();
    try {
      authChannel?.postMessage({ type: "logout" });
      localStorage.setItem("eddkit.logout", String(Date.now())); // Safari fallback (no BroadcastChannel)
    } catch {
      /* ignore */
    }
  }, [qc]);

  // A 401 anywhere forces logout (registered with the axios client).
  useEffect(() => {
    registerUnauthorizedHandler(() => {
      setUser(null);
    });
    return () => registerUnauthorizedHandler(null);
  }, []);

  // Cross-tab logout (Phase 17): the access token is now in memory and the refresh token is an
  // httpOnly cookie, so the old `storage` event on the token key no longer fires. Use a
  // BroadcastChannel (+ a localStorage ping fallback for Safari) to drop this tab's user when another
  // tab signs out.
  useEffect(() => {
    const onLogout = () => {
      clearSession();
      setUser(null);
    };
    if (authChannel) {
      const handler = (e: MessageEvent) => {
        if ((e.data as { type?: string } | null)?.type === "logout") onLogout();
      };
      authChannel.addEventListener("message", handler);
      return () => authChannel?.removeEventListener("message", handler);
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === "eddkit.logout") onLogout();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Resolve the session on first load: ALWAYS attempt one cookie/legacy refresh (the access token is
  // in memory and gone on reload) before deciding logged-in vs out. Gating the authed tree on
  // `initializing` prevents a flash-of-login + a 401 storm from first-paint queries.
  useEffect(() => {
    let active = true;
    bootstrapSession()
      .then((token) => {
        if (!active || !token) return null;
        return fetchMe();
      })
      .then((me) => {
        if (active && me) {
          setUser(me);
          _identifyUser(me);
        }
      })
      .catch(() => {
        if (active) {
          clearSession();
          setUser(null);
        }
      })
      .finally(() => {
        if (active) setInitializing(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(
    async (payload: LoginPayload) => {
      const res = await loginReq(payload);
      // Always store the returned token (restricted when must_change_password).
      setToken(res.access_token);
      // The restricted first-login response has no refresh token; clear any stale one.
      setRefreshToken(res.refresh_token ?? null);
      if (res.must_change_password) {
        // Restricted token — do NOT mark the user authenticated. The LoginPage
        // routes to /set-password where they exchange it for a full token.
        setUser(null);
      } else {
        setUser(res.user);
        _identifyUser(res.user);
      }
      return res;
    },
    [],
  );

  const completePasswordChange = useCallback((res: AuthResponse) => {
    setToken(res.access_token);
    setRefreshToken(res.refresh_token ?? null);
    setUser(res.user);
    _identifyUser(res.user);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      initializing,
      isAuthenticated: !!user,
      login,
      completePasswordChange,
      logout,
    }),
    [user, initializing, login, completePasswordChange, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
